import { betterHumanize } from '@/helpers/dayjs';
import { isNonEmptyString } from '@/helpers/util';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  DialogContentText,
  List,
  ListItem,
  ListItemText,
  TextField,
} from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { seq } from '@tunarr/shared/util';
import dayjs from 'dayjs';
import {
  every,
  identity,
  includes,
  isEmpty,
  map,
  reduce,
  some,
  uniqBy,
  values,
} from 'lodash-es';
import { Trans, useLingui } from '@lingui/react/macro';
import { plural } from '@lingui/core/macro';
import type { HTMLAttributes } from 'react';
import { useMemo, useState } from 'react';
import { match, P } from 'ts-pattern';
import { useCounter } from 'usehooks-ts';
import { extractProgramGrandparent } from '../../helpers/programUtil.ts';
import type { RemoveProgrammingRequest } from '../../hooks/programming_controls/useRemoveProgramming';
import { useRemoveProgramming } from '../../hooks/programming_controls/useRemoveProgramming';
import useStore from '../../store';
import { materializedProgramListSelector } from '../../store/selectors';

type RemoveShowsModalProps = {
  open: boolean;
  onClose: () => void;
};

interface FilmOptionType {
  id: string;
  title: string;
  firstLetter?: string;
}

export const RemoveShowsModal = ({ open, onClose }: RemoveShowsModalProps) => {
  const { t } = useLingui();
  const { count, increment, decrement } = useCounter(0);
  const removeProgramming = useRemoveProgramming();
  const [removeRequest, setRemoveRequest] = useState<RemoveProgrammingRequest>(
    {},
  );
  const programs = useStore(materializedProgramListSelector);
  const hasMovies = useMemo(() => {
    return some(
      programs,
      (p) => p.type === 'content' && p.program.type === 'movie',
    );
  }, [programs]);

  const numAndDurationById = useMemo(() => {
    return reduce(
      programs,
      (acc, curr) => {
        const key = match(curr)
          .with(
            {
              type: 'content',
              program: {
                type: P.select(P.union('movie', 'music_video', 'other_video')),
              },
            },
            identity,
          )
          .with(
            { type: 'content', program: { type: 'episode' } },
            ({ program }) => program.show?.uuid ?? program.season?.show?.uuid,
          )
          .with(
            { type: 'content', program: { type: 'track' } },
            ({ program }) =>
              program.artist?.uuid ?? program.album?.artist?.uuid,
          )
          .with({ type: P._ }, () => undefined)
          .exhaustive();

        if (key) {
          acc[key] ??= { totalDuration: 0, totalPrograms: 0 };
          acc[key].totalDuration += curr.duration;
          acc[key].totalPrograms++;
        }

        return acc;
      },
      {} as Record<string, { totalPrograms: number; totalDuration: number }>,
    );
  }, [programs]);

  // Get the total number of movies from the memoized object
  const movieCount = numAndDurationById['movie']?.totalPrograms ?? 0;

  const showList = useMemo(() => {
    return map(
      uniqBy(
        seq.collect(programs, (program) => {
          if (
            program.type === 'content' &&
            program.program.type === 'episode' &&
            isNonEmptyString(
              program.program.show?.uuid ?? program.program.season?.show?.uuid,
            )
          ) {
            return program;
          }
          return;
        }),
        ({ program }) => extractProgramGrandparent(program)?.uuid,
      ),
      ({ program }) => ({
        // Verified safe above
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        id: extractProgramGrandparent(program)?.uuid!,
        title: extractProgramGrandparent(program)?.title ?? program.title,
      }),
    );
  }, [programs]);

  const artistList = useMemo(() => {
    return map(
      uniqBy(
        seq.collect(programs, (program) => {
          if (
            program.type === 'content' &&
            program.program.type === 'track' &&
            isNonEmptyString(extractProgramGrandparent(program.program)?.uuid)
          ) {
            return program;
          }
          return;
        }),
        ({ program }) => extractProgramGrandparent(program)?.uuid,
      ),
      ({ program }) => ({
        // Verified safe above
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        id: extractProgramGrandparent(program)?.uuid!,
        title: extractProgramGrandparent(program)?.title ?? program.title,
      }),
    );
  }, [programs]);

  const mapListToGroupedOptions = (
    list: Array<{ id: string; title: string }>,
  ) => {
    return list.map((option) => {
      // Get the first letter, use a placeholder if title is empty.
      const firstLetter = (option.title?.[0] ?? '-').toUpperCase();

      return {
        // Group numbers/digits under '0-9', otherwise use the first letter.
        firstLetter: /[0-9]/.test(firstLetter) ? '0-9' : firstLetter,
        ...option,
      };
    });
  };

  const showOptions = useMemo(
    () => mapListToGroupedOptions(showList),
    [showList],
  );

  const artistOptions = useMemo(
    () => mapListToGroupedOptions(artistList),
    [artistList],
  );

  const isEmptyRemoveRequest = useMemo(() => {
    return (
      isEmpty(removeRequest) ??
      every(values(removeRequest), (value) => {
        // If it's a boolean (movies: true/false), only check if it's the 'empty' state (false).
        // We do this because lodash treats booleans as empty regardless of value
        if (typeof value === 'boolean') {
          return value === false;
        }

        // Otherwise, check if the value is empty.
        return isEmpty(value);
      })
    );
  }, [removeRequest]);

  const removeShowsProgramming = () => {
    removeProgramming(removeRequest);
    onClose();
  };

  const getProgramCounts = (id: string, type?: string) => {
    const details = numAndDurationById[id];
    if (!details) {
      return null;
    }

    const count = details.totalPrograms;
    const programLabel = type === 'episode'
      ? plural(count, { one: 'episode', other: 'episodes' })
      : type === 'track'
        ? plural(count, { one: 'track', other: 'tracks' })
        : plural(count, { one: 'program', other: 'programs' });
    return t`${count} ${programLabel}, ${betterHumanize(dayjs.duration(details.totalDuration), {
      style: 'short',
    })}`;
  };

  const getArtistIds = (options: FilmOptionType[]) => map(options, 'id');

  return (
    <>
      <Dialog open={open} scroll={'paper'}>
        <DialogTitle><Trans>Remove Programming</Trans></DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Trans>Pick specific programming to remove from the channel.</Trans>
          </DialogContentText>

          <Autocomplete
            options={showOptions.sort(
              (a, b) => -b.firstLetter.localeCompare(a.firstLetter),
            )}
            groupBy={(option: FilmOptionType) => option.firstLetter ?? '-'}
            getOptionLabel={(option: FilmOptionType) => option.title}
            openOnFocus
            sx={{ my: 2, flex: 1 }}
            autoComplete
            includeInputInList
            multiple={true}
            getOptionDisabled={(option) => {
              const selectedIds = removeRequest.showIds ?? [];
              return includes(selectedIds, option.id);
            }}
            onChange={(_, newOptions) => {
              // Extract the IDs from the selected options
              const newShowIds = map(newOptions, 'id');

              setRemoveRequest((prev) => ({
                ...prev,
                showIds: newShowIds,
              }));

              // Update the program counter based on the number of selected shows
              const countDifference =
                newShowIds.length - (removeRequest.showIds?.length ?? 0);
              if (countDifference > 0) {
                for (let i = 0; i < countDifference; i++) increment();
              } else if (countDifference < 0) {
                for (let i = 0; i < Math.abs(countDifference); i++) decrement();
              }
            }}
            value={
              removeRequest.showIds
                ? showList.filter((show) =>
                    (removeRequest.showIds ?? []).includes(show.id),
                  )
                : []
            }
            renderInput={(params) => (
              <TextField {...params} label={t`Select Shows to Remove`} />
            )}
            renderOption={(
              props: HTMLAttributes<HTMLLIElement>,
              option: FilmOptionType,
            ) => {
              const { ...optionProps } = props;
              return (
                <Box
                  key={option.id}
                  component="li"
                  sx={{
                    width: '100%',
                    flexDirection: ['column', 'row'],
                  }}
                  {...optionProps}
                >
                  <div
                    style={{
                      textAlign: 'left',
                      width: '100%',
                    }}
                  >
                    {option.title}
                  </div>
                  <Chip
                    label={getProgramCounts(option.id, 'episode')}
                    size="small"
                    sx={{ width: '100%' }}
                  />
                </Box>
              );
            }}
          />

          {artistList.length > 0 && (
            <Autocomplete
              options={artistOptions.sort(
                (a, b) => -b.firstLetter.localeCompare(a.firstLetter),
              )}
              groupBy={(option: FilmOptionType) => option.firstLetter ?? '-'}
              getOptionLabel={(option: FilmOptionType) => option.title}
              openOnFocus
              sx={{ my: 2, flex: 1 }}
              autoComplete
              includeInputInList
              multiple={true}
              getOptionDisabled={(option) => {
                const selectedIds = removeRequest.artistIds ?? [];
                return includes(selectedIds, option.id);
              }}
              value={
                removeRequest.artistIds
                  ? artistList.filter((artist) =>
                      (removeRequest.artistIds ?? []).includes(artist.id),
                    )
                  : []
              }
              onChange={(_, newOptions) => {
                const newArtistIds = getArtistIds(newOptions);
                const previousArtistIds = removeRequest.artistIds ?? [];

                setRemoveRequest((prev) => ({
                  ...prev,
                  artistIds: newArtistIds,
                }));

                // Update the program counter based on the number of selected shows
                const countDifference =
                  newArtistIds.length - previousArtistIds.length;

                if (countDifference > 0) {
                  for (let i = 0; i < countDifference; i++) increment();
                } else if (countDifference < 0) {
                  for (let i = 0; i < Math.abs(countDifference); i++)
                    decrement();
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label={t`Select Artists to Remove`} />
              )}
              renderOption={(
                props: HTMLAttributes<HTMLLIElement>,
                option: FilmOptionType,
              ) => {
                const { ...optionProps } = props;
                return (
                  <Box
                    key={option.id}
                    component="li"
                    sx={{
                      width: '100%',
                      flexDirection: ['column', 'row'],
                    }}
                    {...optionProps}
                  >
                    <div
                      style={{
                        textAlign: 'left',
                        width: '100%',
                      }}
                    >
                      {option.title}
                    </div>
                    <Chip
                      label={getProgramCounts(option.id, 'track')}
                      size="small"
                      sx={{ width: '100%' }}
                    />
                  </Box>
                );
              }}
            />
          )}

          <Box key="dynamic-list-container">
            <List dense>
              {hasMovies && (
                <ListItem
                  secondaryAction={
                    <Checkbox
                      edge="end"
                      onChange={(e) => {
                        const isChecked = e.target.checked;

                        setRemoveRequest((prev) => {
                          const newState = {
                            ...prev,
                            movies: isChecked,
                          };
                          return newState;
                        });

                        const countDelta = isChecked ? movieCount : -movieCount;
                        for (let i = 0; i < Math.abs(countDelta); i++) {
                          if (countDelta > 0) increment();
                          else decrement();
                        }
                      }}
                      checked={!!removeRequest.movies}
                    />
                  }
                >
                  <ListItemText
                    primary={t`Remove All ${movieCount} ${plural(movieCount, { one: 'Movie', other: 'Movies' })}`}
                    secondary={getProgramCounts('movies')}
                  />
                </ListItem>
              )}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose()}><Trans>Cancel</Trans></Button>
          <Button
            variant="contained"
            onClick={() => removeShowsProgramming()}
            disabled={isEmptyRemoveRequest}
          >
            <Trans>Remove {count} {plural(count, { one: 'program', other: 'programs' })}</Trans>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
